import { useEffect, useState } from "react";
import { dateOf, SEASON_WEEKS } from "@bd/sim";
import { Icon } from "@bd/ui-kit";
import { useGame } from "../store";
import { setReady } from "./session";
import { t, tp, tdate } from "../i18n";

/** Time left as a person would say it: "11 h 20 min", "4 min", "under 1 min". */
export function timeLeft(deadline: string | null, now: number): string {
  if (!deadline) return "";
  const ms = new Date(deadline).getTime() - now;
  if (ms <= 60_000) return t("under 1 min");
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? t("{h} h {m} min", { h, m }) : t("{m} min", { m });
}

/** Online: takes the clock's place. The date, the quarter's timer, who is ready, and your Ready button. */
export function QuarterBar() {
  const game = useGame((s) => s.game);
  const o = useGame((s) => s.online);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);
  if (!game || !o) return null;
  const d = dateOf(game.week);
  const waitingFor = o.seats.filter((s) => !s.ready && s.seat !== o.seat).map((s) => s.name);
  const total = Math.ceil(SEASON_WEEKS / 13);
  const status =
    o.status === "finished"
      ? t("The season is over")
      : o.settling || o.status === "resolving"
        ? t("Playing out the quarter…")
        : o.ready
          ? waitingFor.length === 0 ? t("Everyone's ready") : waitingFor.length === 1 ? t("Waiting for {name}", { name: waitingFor[0] }) : tp(waitingFor.length, "Waiting for {n} cousin", "Waiting for {n} cousins")
          : t("{time} left", { time: timeLeft(o.deadline, now) });
  return (
    <div className="hud-tr">
      <div className="panel pill clock quarter-bar" data-testid="quarter-bar">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{tdate(d.label)}</div>
          <div className="muted quarter-status" data-testid="quarter-status" title={t("Quarter {n} of {total}. It plays out when everyone is ready or time runs out.", { n: o.quarter + 1, total }) + (waitingFor.length ? ` ${t("Not ready yet: {names}.", { names: waitingFor.join(", ") })}` : "")}>
            {status}
          </div>
        </div>
        <div className="row" style={{ gap: 4 }} aria-label={t("Who is ready")}>
          {o.seats.map((s) => (
            <span key={s.seat} className={`seat-dot ${s.ready ? "ready" : ""} ${s.here ? "here" : ""}`} title={`${s.name} · ${s.company}${s.ready ? ` · ${t("ready")}` : ""}${s.here ? ` · ${t("online now")}` : ""}`} data-testid={`seat-${s.seat}`}>
              {s.ready ? <Icon name="check" size={12} color="#fff" /> : s.name.slice(0, 1).toUpperCase()}
            </span>
          ))}
        </div>
        {o.status !== "finished" && (
          <button
            className={`btn small ${o.ready ? "" : "primary"}`}
            disabled={o.settling || o.status === "resolving"}
            onClick={() => void setReady(!o.ready)}
            data-testid="ready"
            title={o.ready ? t("Undo Ready to change your moves") : t("Done for this quarter. It plays out when everyone is ready or time runs out.")}
            aria-pressed={o.ready}
          >
            {o.ready && <Icon name="check" size={14} color="var(--green)" />}{t("Ready")}
          </button>
        )}
      </div>
    </div>
  );
}
